/**
 * CIC status history stage — structure-scoped, nested in the site dashboard.
 *
 * Rendered as a child route of SiteDashboardLayout, inside the shared
 * dashboard shell (which supplies AppNav + TitleBar). Splits the site's CIC
 * status history into a master grid and a side detail panel; the detail panel
 * can show either the current status or a selected historical entry.
 *
 * Contains:
 *  - CicStatusView — route child component (default export)
 */
import { Box, Paper, Typography } from '@mui/material';
import { Map } from 'immutable';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useUser } from '@sophocles/app';
import LoadingScreen from '@sophocles/components/LoadingScreen';
import { fetchCicStatusForUnit, getCicStatusForSelectedStructure } from '@sophocles/entities/CicStatus';
import {
  cicStatusHistorySelector,
  fetchCicStatusHistoriesForUnitWithRange,
  getCicStatusHistoriesForSelectedStructure,
} from '@sophocles/entities/CicStatusHistory';
import CicStatusDisplay from '@sophocles/views/sites/components/CicStatusDisplay';
import { useSiteDashboardLayoutContext } from '@sophocles/views/sites/SiteDashboardLayout';

import CicStatusGrid from './components/CicStatusGrid';

export function CicStatusView() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useUser();
  const { unit, site, loading } = useSiteDashboardLayoutContext();
  const [detailsOpen, setDetailsOpen] = useState(true);

  const allCicStatusHistories = useSelector(cicStatusHistorySelector);
  const histories = useSelector(getCicStatusHistoriesForSelectedStructure);
  const status = useSelector(getCicStatusForSelectedStructure);

  const unitId = unit?.cic?.id ?? 0;
  const unitTimeZone = site?.time_zone ?? 'UTC';
  const cicUnit = unit?.cic ?? Map();

  const selectedStatusHistoryId = searchParams.get('id') || false;
  const selectedStatus = selectedStatusHistoryId
    ? allCicStatusHistories.get(selectedStatusHistoryId.toString(), Map())
    : status;

  const fetchData = useCallback(
    (uId, from, to) => dispatch(fetchCicStatusHistoriesForUnitWithRange(uId, from, to)),
    [dispatch],
  );
  const fetchStatus = useCallback(uId => dispatch(fetchCicStatusForUnit(uId)), [dispatch]);

  const openDetails = useCallback(
    statusHist => {
      const params = new URLSearchParams(location.search);
      if (statusHist) {
        params.set('id', statusHist);
      } else {
        params.delete('id');
      }
      setDetailsOpen(true);
      navigate({ pathname: location.pathname, search: params.toString() });
    },
    [location, navigate],
  );

  const closeDetails = useCallback(() => setDetailsOpen(false), []);
  const showCurrentStatus = useCallback(() => openDetails(false), [openDetails]);

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <Box sx={{ pb: 2, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {unitId != 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'row', flex: 1, minHeight: 0, gap: 2 }}>
          {/* Master list: below md there is no room for a side-by-side split,
              so an open detail panel takes over and the list hides (the
              detail's close button restores it). */}
          <Box
            sx={{
              width: { xs: '100%', md: detailsOpen ? 550 : '100%' },
              display: detailsOpen ? { xs: 'none', md: 'flex' } : 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <CicStatusGrid
              unitId={unitId}
              histories={histories}
              fetchData={fetchData}
              showFullList={!detailsOpen}
              openDetails={openDetails}
            />
          </Box>
          {detailsOpen ? (
            <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
              <Paper sx={{ width: '100%', height: '100%', backgroundColor: 'background.paper', p: '8px 12px' }}>
                <CicStatusDisplay
                  isCurrent={!selectedStatusHistoryId}
                  unitId={unitId}
                  unit={cicUnit}
                  user={user}
                  status={selectedStatus}
                  unitTimeZone={unitTimeZone}
                  fetchStatus={fetchStatus}
                  showCurrent={showCurrentStatus}
                  close={closeDetails}
                />
              </Paper>
            </Box>
          ) : null}
        </Box>
      ) : (
        <Typography variant="h5" align="center" sx={{ mt: 4 }}>
          {t('pages.noStandaloneCicFound')}
        </Typography>
      )}
    </Box>
  );
}

export default CicStatusView;
